import { useRecoilValue } from "recoil";
import { FlexContext } from "../models/flexible/flex-context";
import { AgreementScope } from "../models/generated/graphql";
import { flexibleFormResponseState } from "../store/flexible";
import { userPersonalInfoState } from "../store/user";
import {
	isNotRestrictedByRACASingleBrandScopeRule,
	isScopeExactlyMatchedByDrugs,
	isScopeMatchedByDisease,
	isScopeMatchedByDrugs,
	isScopeMatchedByIndication,
	isScopeMatchedByScopeId,
} from "../utils/filter-scopes";
import { useScopeFields } from "./useScopeFields";

export const useScopes = (context: FlexContext) => {
	const { unfilteredBrands, unfilteredGenerics, indication, scopeId, disease } = useScopeFields(context);
	const user = useRecoilValue(userPersonalInfoState);
	const flexFormResponse: { agreementScopes?: Array<AgreementScope> } = useRecoilValue(flexibleFormResponseState);
	const entityTypeId = context.entityType.id;

	const scopes =
		flexFormResponse.agreementScopes
			?.filter(
				(scope) =>
					!entityTypeId ||
					!!scope.enrollingConditionSets.some((conditionSet) =>
						[...(conditionSet.agreement?.allowedForms || []), ...(conditionSet.agreement?.allowedRecords || [])].includes(
							entityTypeId
						)
					)
			)
			.map((scope) => ({
				...scope,
				orgScopes: scope.orgScopes?.filter((orgScope) => orgScope.orgId === user.organization.externalId),
				enrollingConditionSets: scope.enrollingConditionSets.filter((conditionSet) => !!conditionSet.agreement),
			})) || [];

	const selectedScopes = scopes
		.filter(
			(scope) =>
				isScopeMatchedByDrugs(scope, unfilteredBrands, unfilteredGenerics) &&
				isScopeMatchedByScopeId(scope, scopeId) &&
				isScopeMatchedByIndication(scope, indication) &&
				isScopeMatchedByDisease(scope, disease) &&
				isNotRestrictedByRACASingleBrandScopeRule(scope, context.entityType.id)
		)
		.filter(
			(scope) =>
				context.isNegotiable ||
				context.entityType.isStartingPoint ||
				context.cases.some((cs) =>
					scope.enrollingConditionSets.some(
						(conditionSet) => conditionSet.agreement?.initialVersionId === cs.baseAgreement.initialVersionId
					)
				)
		);

	return {
		selectedScopes,
		selectedScopesExactlyMatchedByDrugs: selectedScopes.filter((scope) =>
			isScopeExactlyMatchedByDrugs(scope, unfilteredBrands, unfilteredGenerics)
		),
		allScopes: scopes,
	};
};
